import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { StatusBadge, fmtCurrency } from "../lib/status";
import { Plus, X, Car as CarIcon, Zap, AlertOctagon, ClipboardList, Upload, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Sedan", "Hatchback", "SUV", "Pickup", "Van", "Scooter", "Motorcycle", "Other"];
const TRANSMISSIONS = ["Automatic", "Manual", "Semi-automatic"];
const FUEL_TYPES = ["", "gasoline", "diesel", "electric", "hybrid", "other"];
const PLATE_DISPLAY_MODES = [
  { value: "hidden",          label: "Hide plate publicly" },
  { value: "last_chars",      label: "Show last 3 chars (•••XYZ)" },
  { value: "internal_only",   label: "Internal use only (never public)" },
];
const STATUSES = ["available", "booked", "in_use", "returned_needs_check", "maintenance", "damaged", "unavailable"];

const FORM_TABS = [
  { key: "basic",     label: "Basic Info" },
  { key: "technical", label: "Technical" },
  { key: "pricing",   label: "Pricing" },
  { key: "public",    label: "Public Description" },
  { key: "rules",     label: "Rules & Pickup" },
  { key: "internal",  label: "Internal Notes" },
  { key: "visibility", label: "Visibility" },
];

const POINT_COSTS = {
  motorcycle: 1, scooter: 1,
  sedan: 3, hatchback: 3, small_car: 3,
  suv: 5, pickup: 5,
  van: 6, minibus: 8, luxury: 10, other: 3,
};
const pointCost = (cat) => POINT_COSTS[(cat || "other").toLowerCase().replace(/ /g, "_")] ?? 3;

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [initialTab, setInitialTab] = useState("basic");
  const [fleet, setFleet] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, f] = await Promise.all([
        api.get("/vehicles"),
        api.get("/vehicles/fleet-usage").catch(() => ({ data: null })),
      ]);
      setVehicles(v.data);
      setFleet(f.data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await load(); })();
    return () => { cancelled = true; };
  }, [load]);

  // Deep-link: ?edit=<vehicle_id>&tab=<tab_key> opens the edit modal on the
  // requested tab. Used by "Edit public details" shortcut on the vehicle
  // detail page so owners go straight to the right section.
  const openDeepLinkEdit = useCallback(() => {
    if (loading) return;
    const editId = searchParams.get("edit");
    if (!editId) return;
    const target = vehicles.find((v) => v.id === editId);
    if (!target) return;
    setEditing(target);
    setInitialTab(searchParams.get("tab") || "basic");
    setShowForm(true);
    const next = new URLSearchParams(searchParams);
    next.delete("edit"); next.delete("tab");
    setSearchParams(next, { replace: true });
  }, [loading, vehicles, searchParams, setSearchParams]);
  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) openDeepLinkEdit(); })();
    return () => { cancelled = true; };
  }, [openDeepLinkEdit]);

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Vehicles</h1>
          <p className="text-slate-600 mt-1">Manage your fleet and their availability.</p>
        </div>
        <div className="flex flex-wrap gap-2"><Link to="/app/vehicles/import" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-sm font-medium hover:bg-slate-50"><Upload className="w-4 h-4" /> Import CSV</Link><button
          data-testid="vehicle-add-btn"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg btn-brand font-medium"
        >
          <Plus className="w-4 h-4" /> Add vehicle
        </button></div>
      </div>

      {loading && <div className="text-slate-500">Loading…</div>}

      {!loading && fleet && (
        <FleetUsageCard fleet={fleet} />
      )}

      {!loading && vehicles.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="vehicles-empty">
          <CarIcon className="w-8 h-8 mx-auto text-slate-400" />
          <p className="mt-2 text-slate-700 font-medium">No vehicles yet</p>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Add your vehicles first so bookings can be connected to the right car, motor, van, or SUV.
          </p>
          <button
            onClick={() => setShowForm(true)}
            data-testid="vehicles-empty-cta"
            className="mt-3 inline-flex items-center gap-1 px-4 py-2 rounded-lg btn-brand text-sm font-medium">
            + Add Vehicle
          </button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {vehicles.map((v) => (
          <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-5 hover:shadow-md transition-shadow" data-testid={`vehicle-card-${v.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-display text-lg font-semibold">{v.name}</h3>
                <p className="text-xs text-slate-500 font-mono">{v.plate_number || "No plate"}</p>
              </div>
              <StatusBadge status={v.status} type="vehicle" />
            </div>
            {v.safety_hold_active && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-[11px] font-semibold"
                    data-testid={`vehicle-card-safety-hold-${v.id}`}>
                <ShieldAlert className="w-3.5 h-3.5" /> SAFETY HOLD · Do Not Hand Over
              </div>
            )}
            <dl className="mt-4 grid grid-cols-3 gap-2 text-xs text-slate-600">
              <div>
                <dt className="uppercase tracking-wider text-[10px] text-slate-400">Category</dt>
                <dd className="text-slate-900">{v.category}</dd>
              </div>
              <div>
                <dt className="uppercase tracking-wider text-[10px] text-slate-400">Trans</dt>
                <dd className="text-slate-900">{v.transmission}</dd>
              </div>
              <div>
                <dt className="uppercase tracking-wider text-[10px] text-slate-400">Seats</dt>
                <dd className="text-slate-900">{v.seats}</dd>
              </div>
            </dl>
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm font-mono">{fmtCurrency(v.price_per_day)}<span className="text-slate-500 text-xs"> /day</span></div>
              <div className="flex gap-2">
                <Link to={`/app/vehicles/${v.id}/damages`} className="text-xs text-slate-700 hover:underline inline-flex items-center gap-0.5" data-testid={`vehicle-damages-${v.id}`}>
                  <ClipboardList className="w-3 h-3" /> Damage History
                </Link>
                <Link to={`/app/vehicles/${v.id}`} className="text-sm text-[#1B4332] font-medium hover:underline" data-testid={`vehicle-view-${v.id}`}>View</Link>
                <button onClick={() => { setEditing(v); setShowForm(true); }} className="text-sm text-slate-700 hover:underline" data-testid={`vehicle-edit-${v.id}`}>Edit</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <VehicleFormModal
          initial={editing}
          initialTab={initialTab}
          onClose={() => { setShowForm(false); setInitialTab("basic"); }}
          onSaved={() => { setShowForm(false); setInitialTab("basic"); load(); }}
        />
      )}
    </div>
  );
}

function VehicleFormModal({ initial, initialTab = "basic", onClose, onSaved }) {
  const { company } = useAuth();
  const navigate = useNavigate();
  const defaultRates = company?.default_rates_by_category || {};
  const initialCategory = initial?.category || "Sedan";
  const [tab, setTab] = useState(initialTab);
  const [form, setForm] = useState(initial || {
    name: "", plate_number: "", category: initialCategory, transmission: "Automatic",
    seats: 5,
    price_per_day: defaultRates[initialCategory] ?? 2500,
    status: "available", notes: "",
    // New defaults
    make: "", model: "", variant: "", year: "", color: "",
    plate_display_mode: "hidden",
    fuel_type: "", doors: "", engine_cc: "",
    luggage_capacity: "", has_aircon: false, helmet_included: false, allowed_passengers: "",
    price_per_hour: "", weekly_rate: "", monthly_rate: "", deposit_amount: "",
    extra_day_rate: "", late_return_fee: "", delivery_fee: "",
    pickup_delivery_notes: "", allowed_travel_area: "", restricted_travel_area: "",
    fuel_policy_note: "", mileage_limit: "", excess_km_fee: "",
    public_description: "", public_notes: "",
    public_features: [], included_items: [],
    requirements_to_rent: "", special_rules: "", recommended_use: "",
    internal_notes: "", maintenance_notes: "", staff_notes: "", insurance_notes: "",
    public_listing_enabled: true, show_price_publicly: true,
    show_deposit_publicly: true, show_availability_publicly: true,
    show_details_publicly: true,
  });
  const [priceManual, setPriceManual] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => {
    const target = e.target;
    let raw;
    if (target.type === "number") raw = target.value === "" ? "" : Number(target.value);
    else if (target.type === "checkbox") raw = target.checked;
    else raw = target.value;
    if (k === "category" && !initial && !priceManual && defaultRates[raw]) {
      setForm((f) => ({ ...f, category: raw, price_per_day: defaultRates[raw] }));
      return;
    }
    if (k === "price_per_day") setPriceManual(true);
    setForm((f) => ({ ...f, [k]: raw }));
  };
  const setList = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const useCategoryDefault = () => {
    const rate = defaultRates[form.category];
    if (rate) {
      setForm((f) => ({ ...f, price_per_day: rate }));
      setPriceManual(false);
    } else {
      toast.info(`No default rate set for "${form.category}". Add one in Settings.`);
    }
  };

  // Normalise the payload: empty strings on numeric fields → null so Pydantic
  // doesn't reject them.
  const _clean = (raw) => {
    const numericKeys = [
      "year", "doors", "engine_cc", "seats", "allowed_passengers",
      "price_per_hour", "weekly_rate", "monthly_rate", "deposit_amount",
      "extra_day_rate", "late_return_fee", "delivery_fee", "excess_km_fee",
    ];
    const out = { ...raw };
    for (const k of numericKeys) {
      if (out[k] === "" || out[k] === undefined) out[k] = null;
    }
    return out;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = _clean(form);
      if (initial) {
        await api.put(`/vehicles/${initial.id}`, payload);
        toast.success("Vehicle updated");
        onSaved();
      } else {
        const res = await api.post("/vehicles", payload);
        toast.success("Vehicle added", {
          description: "Log any known damages now so the customer can review them at pickup.",
          action: {
            label: "Open damage sheet",
            onClick: () => navigate(`/app/vehicles/${res.data.id}/damages`),
          },
          duration: 8000,
        });
        onSaved();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error_code === "fleet_capacity_exceeded") {
        toast.error(detail.message, { duration: 6000 });
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 overflow-y-auto" data-testid="vehicle-form-modal">
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-slate-200 max-w-3xl w-full my-4 overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-slate-200">
            <h2 className="font-display text-xl font-semibold">{initial ? "Edit vehicle" : "Add vehicle"}</h2>
            <button onClick={onClose} className="grid min-h-11 min-w-11 place-items-center rounded-md hover:bg-slate-100" data-testid="vf-close"><X className="w-4 h-4" /></button>
          </div>

          {/* Tabs */}
          <div className="px-5 pt-3 border-b border-slate-200 overflow-x-auto" data-testid="vf-tabs">
            <div className="flex gap-1 min-w-max">
              {FORM_TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  data-testid={`vf-tab-${t.key}`}
                  className={`px-3 py-1.5 text-sm rounded-t-md border-b-2 -mb-px whitespace-nowrap transition ${
                    tab === t.key
                      ? "border-[#2D6A4F] text-[#1B4332] font-semibold"
                      : "border-transparent text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={submit} className="p-5">
            {tab === "basic" && (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="vf-section-basic">
                <F label="Name" required testId="vf-name" value={form.name} onChange={set("name")} className="sm:col-span-2" />
                <F label="Make / brand" testId="vf-make" value={form.make || ""} onChange={set("make")} />
                <F label="Model" testId="vf-model" value={form.model || ""} onChange={set("model")} />
                <F label="Variant / trim" testId="vf-variant" value={form.variant || ""} onChange={set("variant")} />
                <F label="Year" type="number" testId="vf-year" value={form.year || ""} onChange={set("year")} />
                <F label="Color" testId="vf-color" value={form.color || ""} onChange={set("color")} />
                <S label="Category" testId="vf-category" value={form.category} onChange={set("category")} options={CATEGORIES} />
                <F label="Plate number" testId="vf-plate" value={form.plate_number || ""} onChange={set("plate_number")} />
                <label>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Public plate display</span>
                  <select data-testid="vf-plate-display"
                          value={form.plate_display_mode || "hidden"}
                          onChange={set("plate_display_mode")}
                          className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none">
                    {PLATE_DISPLAY_MODES.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </label>
                <S label="Status" testId="vf-status" value={form.status} onChange={set("status")} options={STATUSES} />
              </div>
            )}

            {tab === "technical" && (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="vf-section-technical">
                <S label="Transmission" testId="vf-transmission" value={form.transmission} onChange={set("transmission")} options={TRANSMISSIONS} />
                <label>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Fuel type</span>
                  <select data-testid="vf-fuel"
                          value={form.fuel_type || ""}
                          onChange={set("fuel_type")}
                          className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none">
                    {FUEL_TYPES.map((f) => <option key={f} value={f}>{f || "-"}</option>)}
                  </select>
                </label>
                <F label="Seats" type="number" testId="vf-seats" value={form.seats || ""} onChange={set("seats")} />
                <F label="Doors" type="number" testId="vf-doors" value={form.doors || ""} onChange={set("doors")} />
                <F label="Engine CC (for motorbikes)" type="number" testId="vf-engine-cc" value={form.engine_cc || ""} onChange={set("engine_cc")} />
                <F label="Allowed passengers" type="number" testId="vf-allowed-pass" value={form.allowed_passengers || ""} onChange={set("allowed_passengers")} />
                <F label="Luggage capacity" testId="vf-luggage" value={form.luggage_capacity || ""} onChange={set("luggage_capacity")} className="sm:col-span-2" />
                <CheckRow label="Air-conditioning" testId="vf-aircon" checked={!!form.has_aircon} onChange={set("has_aircon")} />
                <CheckRow label="Helmet included (for motorbikes)" testId="vf-helmet" checked={!!form.helmet_included} onChange={set("helmet_included")} />
              </div>
            )}

            {tab === "pricing" && (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="vf-section-pricing">
                <label className="sm:col-span-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Price per day (PHP)</span>
                  <input data-testid="vf-price" type="number" required value={form.price_per_day} onChange={set("price_per_day")}
                         className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
                  {defaultRates[form.category] ? (
                    defaultRates[form.category] === Number(form.price_per_day) ? (
                      <p className="text-[11px] text-emerald-700 mt-1" data-testid="vf-price-using-default">
                        Using <b>{form.category}</b> default ({fmtCurrency(defaultRates[form.category])}/day).
                      </p>
                    ) : (
                      <button type="button" onClick={useCategoryDefault} data-testid="vf-price-use-default"
                              className="text-[11px] text-[#1B4332] hover:underline mt-1">
                        Use {form.category} default ({fmtCurrency(defaultRates[form.category])}/day)
                      </button>
                    )
                  ) : (
                    <p className="text-[11px] text-slate-500 mt-1">
                      No default rate set for <b>{form.category}</b>. Add one in <Link to="/app/settings" className="text-[#1B4332] hover:underline">Settings</Link>.
                    </p>
                  )}
                </label>
                <F label="Price per hour" type="number" testId="vf-price-hour" value={form.price_per_hour || ""} onChange={set("price_per_hour")} />
                <F label="Deposit amount" type="number" testId="vf-deposit" value={form.deposit_amount || ""} onChange={set("deposit_amount")} />
                <F label="Weekly rate" type="number" testId="vf-weekly" value={form.weekly_rate || ""} onChange={set("weekly_rate")} />
                <F label="Monthly rate" type="number" testId="vf-monthly" value={form.monthly_rate || ""} onChange={set("monthly_rate")} />
                <F label="Extra day rate" type="number" testId="vf-extra-day" value={form.extra_day_rate || ""} onChange={set("extra_day_rate")} />
                <F label="Late return fee" type="number" testId="vf-late-fee" value={form.late_return_fee || ""} onChange={set("late_return_fee")} />
                <F label="Delivery fee" type="number" testId="vf-delivery-fee" value={form.delivery_fee || ""} onChange={set("delivery_fee")} />
                <F label="Excess km fee" type="number" testId="vf-excess-km" value={form.excess_km_fee || ""} onChange={set("excess_km_fee")} />
                <div className="sm:col-span-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700 flex items-center gap-2" data-testid="vf-fleet-cost-hint">
                  <Zap className="w-3.5 h-3.5 text-amber-500" />
                  <span>This <b>{form.category}</b> will use <b>{pointCost(form.category)}</b> fleet point{pointCost(form.category) === 1 ? "" : "s"}.</span>
                  <Link to="/app/billing" className="ml-auto text-[#1B4332] hover:underline" data-testid="vf-billing-link">Manage capacity →</Link>
                </div>
              </div>
            )}

            {tab === "public" && (
              <div className="space-y-4" data-testid="vf-section-public">
                <T label="Public description"           rows={3} testId="vf-public-desc"   value={form.public_description || ""} onChange={set("public_description")}  hint="Shown publicly on the vehicle detail card. 1–3 sentences." />
                <T label="Public notes"                 rows={2} testId="vf-public-notes"  value={form.public_notes || ""}        onChange={set("public_notes")} />
                <Chips label="Public features"          testId="vf-features"   values={form.public_features || []}  onChange={setList("public_features")}  placeholder="Air-conditioning, automatic transmission…" />
                <Chips label="Included items / accessories" testId="vf-included" values={form.included_items || []}   onChange={setList("included_items")} placeholder="1 helmet, spare tyre…" />
                <F  label="Recommended use"             testId="vf-recommended" value={form.recommended_use || ""}   onChange={set("recommended_use")} />
              </div>
            )}

            {tab === "rules" && (
              <div className="space-y-4" data-testid="vf-section-rules">
                <T label="Pickup / delivery notes"      rows={2} testId="vf-pickup-notes" value={form.pickup_delivery_notes || ""} onChange={set("pickup_delivery_notes")} />
                <T label="Allowed travel area"          rows={2} testId="vf-allowed-area" value={form.allowed_travel_area || ""}   onChange={set("allowed_travel_area")} />
                <T label="Restricted travel area"       rows={2} testId="vf-restricted-area" value={form.restricted_travel_area || ""} onChange={set("restricted_travel_area")} />
                <T label="Fuel policy"                  rows={2} testId="vf-fuel-policy" value={form.fuel_policy_note || ""}     onChange={set("fuel_policy_note")} />
                <F label="Mileage / km limit"           testId="vf-mileage-limit" value={form.mileage_limit || ""} onChange={set("mileage_limit")} />
                <T label="Requirements to rent"         rows={2} testId="vf-requirements" value={form.requirements_to_rent || ""} onChange={set("requirements_to_rent")} />
                <T label="Special rules"                rows={2} testId="vf-special-rules" value={form.special_rules || ""} onChange={set("special_rules")} />
              </div>
            )}

            {tab === "internal" && (
              <div className="space-y-4" data-testid="vf-section-internal">
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                  These notes are only visible to your business team. They are never shown publicly.
                </div>
                <T label="Notes (general)"      rows={2} testId="vf-notes"      value={form.notes || ""}            onChange={set("notes")} />
                <T label="Internal notes"       rows={3} testId="vf-internal"   value={form.internal_notes || ""}   onChange={set("internal_notes")} />
                <T label="Maintenance notes"    rows={3} testId="vf-maintenance" value={form.maintenance_notes || ""} onChange={set("maintenance_notes")} />
                <T label="Staff notes"          rows={2} testId="vf-staff"      value={form.staff_notes || ""}      onChange={set("staff_notes")} />
                <T label="Insurance notes"      rows={2} testId="vf-insurance"  value={form.insurance_notes || ""}  onChange={set("insurance_notes")} />
              </div>
            )}

            {tab === "visibility" && (
              <div className="space-y-3" data-testid="vf-section-visibility">
                <p className="text-xs text-slate-600">Control what renters can see for this vehicle on your public profile.</p>
                <CheckRow label="List this vehicle publicly"   testId="vf-vis-list"     checked={form.public_listing_enabled !== false} onChange={set("public_listing_enabled")} />
                <CheckRow label="Show price publicly"          testId="vf-vis-price"    checked={form.show_price_publicly !== false}    onChange={set("show_price_publicly")} />
                <CheckRow label="Show deposit publicly"        testId="vf-vis-deposit"  checked={form.show_deposit_publicly !== false}  onChange={set("show_deposit_publicly")} />
                <CheckRow label="Show availability/status publicly" testId="vf-vis-status" checked={form.show_availability_publicly !== false} onChange={set("show_availability_publicly")} />
                <CheckRow label="Show vehicle details publicly" testId="vf-vis-details" checked={form.show_details_publicly !== false}  onChange={set("show_details_publicly")} />
              </div>
            )}

            <div className="mt-6 flex justify-end gap-2 border-t border-slate-200 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">Cancel</button>
              <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-lg btn-brand font-medium disabled:opacity-60" data-testid="vf-submit">
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function CheckRow({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
      <input type="checkbox" data-testid={testId} checked={!!checked} onChange={onChange} />
      <span>{label}</span>
    </label>
  );
}

function T({ label, value, onChange, rows = 2, testId, hint }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <textarea data-testid={testId} value={value} onChange={onChange} rows={rows}
                className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </label>
  );
}

function Chips({ label, values, onChange, testId, placeholder }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (values.includes(v)) { setDraft(""); return; }
    onChange([...values, v]);
    setDraft("");
  };
  const removeAt = (i) => onChange(values.filter((_, j) => j !== i));
  return (
    <div>
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <div className="mt-1.5 flex gap-1.5 flex-wrap">
        {values.map((v, i) => (
          <span key={i} className="px-2 py-1 rounded-full bg-slate-100 text-xs inline-flex items-center gap-1" data-testid={`${testId}-chip-${i}`}>
            {v}
            <button type="button" onClick={() => removeAt(i)} className="text-slate-500 hover:text-rose-600">×</button>
          </span>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <input data-testid={`${testId}-input`} value={draft} onChange={(e) => setDraft(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
               placeholder={placeholder}
               className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-[#2D6A4F] outline-none" />
        <button type="button" onClick={add} data-testid={`${testId}-add`}
                className="px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">Add</button>
      </div>
    </div>
  );
}

function F({ label, value, onChange, type = "text", required, testId, className = "" }) {
  return (
    <label className={className}>
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <input data-testid={testId} type={type} required={required} value={value} onChange={onChange}
        className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
    </label>
  );
}
function S({ label, value, onChange, options, testId }) {
  return (
    <label>
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <select data-testid={testId} value={value} onChange={onChange} className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none">
        {options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
      </select>
    </label>
  );
}

function FleetUsageCard({ fleet }) {
  if (fleet.unlimited) {
    return (
      <div data-testid="fleet-usage-card" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 inline-flex items-center gap-2">
        <Zap className="w-4 h-4" /> <b>Unlimited fleet capacity</b> on the {fleet.plan_label} plan.
      </div>
    );
  }
  const pct = Math.round(((fleet.used || 0) / Math.max(1, fleet.total || 1)) * 100);
  const tone = pct >= 100 ? "rose" : pct >= 80 ? "amber" : "emerald";
  const bg = {
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  }[tone];
  const bar = { rose: "bg-rose-500", amber: "bg-amber-500", emerald: "bg-emerald-500" }[tone];

  return (
    <div data-testid="fleet-usage-card" className={`rounded-xl border p-4 ${bg}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex items-center gap-2 text-sm font-medium">
          {tone === "rose" ? <AlertOctagon className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
          Fleet capacity ({fleet.plan_label}): <b data-testid="fleet-usage-numbers">{fleet.used} / {fleet.total} pts</b>
          <span className="opacity-70">· {pct}%</span>
        </div>
        <Link to="/app/billing" data-testid="fleet-billing-link" className="text-xs px-2.5 py-1 rounded-md bg-white/70 border border-current/30 hover:bg-white font-medium">
          Manage plan / buy points →
        </Link>
      </div>
      <div className="mt-2 h-2 rounded-full bg-white/70 overflow-hidden">
        <div className={`h-full ${bar}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      {fleet.breakdown?.length > 0 && (
        <div className="mt-2 text-xs flex flex-wrap gap-2">
          {fleet.breakdown.map((b) => (
            <span key={b.category} className="px-2 py-0.5 bg-white/60 rounded-full" data-testid={`fleet-pill-${b.category}`}>
              {b.category}: {b.count} × {b.cost_each} = {b.subtotal}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
